import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { CheckCircle, ExternalLink, Info } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGoogleLogin } from '@react-oauth/google';
import { connectGoogleIntegration, disconnectGoogleAds, refreshGoogleAdsAccounts } from '@/lib/apis';
import { useToast } from '@/hooks/useToast';
import { usePostHog } from 'posthog-js/react';
import googleAds from '/google-ads.svg';

interface GoogleAdsIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  connected: boolean;
  accountName?: string | null;
}

// The single OAuth scope the Google connect flow requests (see useGoogleLogin below). Google's
// connection is just a refresh token on the user row — unlike Meta/HubSpot we don't persist a
// granted-scope list server-side — but a working connection implies exactly this grant, so the
// "Granted Permissions" chip renders it statically for visual parity with the other two modals.
const GOOGLE_ADS_OAUTH_SCOPE = 'https://www.googleapis.com/auth/adwords';

/**
 * Google Ads integration modal (#5936) — structural mirror of MetaIntegrationModal /
 * HubSpotIntegrationModal: status + permissions, Refresh Connection (outline) above Disconnect
 * (red), Connect CTA when disconnected.
 *
 * Deliberately NO account switching in any integration modal: these modals own the connection
 * lifecycle only. Ad-account selection lives in the user menu ("Ad Accounts → Google / Meta",
 * opening GoogleAdAccountSelectionModal / MetaAdAccountSelectionModal) — the same split Meta
 * established in #4173 — plus the org-overview Switch (#5926). HubSpot has no account concept
 * at all: its OAuth grant is portal-scoped, so changing portal IS reconnecting.
 */
export function GoogleAdsIntegrationModal({ isOpen, onClose, connected, accountName }: GoogleAdsIntegrationModalProps) {
  const posthog = usePostHog();
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const invalidateConnectionState = () => {
    // /user/me carries google_ads_connected + google_connection_broken (#5935/#5936);
    // the accounts list drives the picker and the top-bar switcher.
    queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
    queryClient.invalidateQueries({ queryKey: ['googleAdsAccounts'] });
  };

  // Connect / Refresh Connection share one OAuth flow: exchange the auth-code (JWT-anchored,
  // #4152), re-scan accessible accounts, refresh the connection state everywhere.
  const oauthLogin = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        await connectGoogleIntegration(codeResponse.code);
        await refreshGoogleAdsAccounts();
        posthog?.capture('google_ads_reconnected');
        // No success toast (house rule): the visible confirmation is what happens next —
        // the invalidation flips the profile to connected, and for a fresh connect the
        // Settings page auto-opens the account picker (#5937, Meta parity), so the modal
        // closes out of the way. A Refresh on an already-connected account keeps it open.
        invalidateConnectionState();
        if (!connected) onClose();
      } catch (error) {
        toast({
          title: 'Connection Failed',
          description: error instanceof Error ? error.message : 'Failed to connect Google Ads',
          variant: 'destructive',
        });
      } finally {
        setIsAuthorizing(false);
      }
    },
    onError: () => setIsAuthorizing(false),
    onNonOAuthError: () => setIsAuthorizing(false),
    scope: 'https://www.googleapis.com/auth/adwords',
    flow: 'auth-code',
    include_granted_scopes: false,
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectGoogleAds,
    onSuccess: (data) => {
      // success=false rides a 200 (e.g. a stale tab disconnecting an already-disconnected
      // integration), so it must be handled here — a throw inside onSuccess escapes React
      // Query's onError and crashes the component. Invalidate anyway so the stale UI resyncs.
      if (!data.success) {
        toast({ title: 'Disconnection Failed', description: data.message, variant: 'destructive' });
        invalidateConnectionState();
        return;
      }
      posthog?.capture('google_ads_disconnected');
      // No success toast (house rule): the card flipping to disconnected is the confirmation.
      invalidateConnectionState();
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Disconnection Failed',
        description: error.message || 'Failed to disconnect Google Ads',
        variant: 'destructive',
      });
    },
  });

  const handleOAuth = () => {
    setIsAuthorizing(true);
    oauthLogin();
  };

  const isLoading = isAuthorizing || disconnectMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <img src={googleAds} alt="Google Ads" className="w-6 h-6" />
            Google Ads Integration
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Connection Status */}
          <div className="flex items-start gap-3 p-4 border rounded-lg">
            {connected ? (
              <>
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Connected</p>
                  <p className="text-sm text-gray-700">{accountName || 'Google Ads account'}</p>
                </div>
              </>
            ) : (
              <>
                <Info className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Not Connected</p>
                  <p className="text-sm text-gray-500">
                    Connect a Google Ads account to create and deploy campaigns.
                  </p>
                </div>
              </>
            )}
          </div>

          {connected && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-900">Granted Permissions:</h4>
              {/* Single blue chip — same rendering as the Meta/HubSpot modals. Static (see
                  GOOGLE_ADS_OAUTH_SCOPE): the scope isn't persisted server-side for Google. */}
              <div className="flex flex-wrap gap-1">
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-md">
                  {GOOGLE_ADS_OAUTH_SCOPE}
                </span>
              </div>
            </div>
          )}

          {/* Actions — mirrors Meta/HubSpot: Refresh Connection (outline) above Disconnect (red). */}
          <div className="flex flex-col gap-3">
            {!connected ? (
              <Button onClick={handleOAuth} disabled={isLoading} className="w-full">
                {isLoading ? (
                  <>
                    <Spinner className="w-4 h-4 mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Connect Google Ads
                  </>
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button onClick={handleOAuth} disabled={isLoading} variant="outline" className="w-full">
                  {isAuthorizing ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Refreshing...
                    </>
                  ) : (
                    'Refresh Connection'
                  )}
                </Button>

                <Button
                  onClick={() => disconnectMutation.mutate()}
                  disabled={isLoading}
                  variant="destructive"
                  className="w-full"
                >
                  {disconnectMutation.isPending ? (
                    <>
                      <Spinner className="w-4 h-4 mr-2" />
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect Google Ads'
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Help Text — same three-bullet pattern as the Meta/HubSpot modals. Their first bullet
              says "encrypted and stored securely" because those tokens are encrypted at rest
              (IntegrationTokenEncryption); the Google refresh token currently is not, so the
              encryption claim is deliberately omitted here. */}
          <div className="text-xs text-gray-500 space-y-1">
            <p>• Your Google Ads credentials are stored securely</p>
            <p>• You can disconnect at any time from this settings page</p>
            <p>• Only the permissions you grant will be accessible</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
